import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Card as BaseCard, 
  Textarea
} from '../../../../styles/components';
import { glassDeep } from '../../../../styles/components/common/glassmorphism';
import { fadeInAnimation } from '../../../../styles/components/common/animations';

const NotesContainer = styled(BaseCard)`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  height: 100%;
  ${glassDeep}
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.textOnDark};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const SaveIndicator = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all 0.3s ease;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  &.saving {
    color: ${({ theme }) => theme.colors.primaryLight};
    animation: ${fadeInAnimation} 0.5s ease-out;
  }
`;

const TextArea = styled(Textarea)`
  flex: 1;
  resize: none;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme }) => theme.colors.textOnDark};
  background: rgba(15, 23, 42, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Notes = ({ workshopId, onSave, initialValue = '', ...props }) => {
  const [notes, setNotes] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    const savedNotes = localStorage.getItem(`workshop-notes-${workshopId}`);
    if (savedNotes && !initialValue) {
      setNotes(savedNotes);
    }
  }, [workshopId, initialValue]);

  useEffect(() => {
    const saveTimeout = setTimeout(() => {
      if (notes !== initialValue) {
        setIsSaving(true);
        localStorage.setItem(`workshop-notes-${workshopId}`, notes);
        onSave?.(notes);
        setLastSaved(new Date());
        setIsSaving(false);
      }
    }, 1000);

    return () => clearTimeout(saveTimeout);
  }, [notes, workshopId, onSave, initialValue]);

  const formatLastSaved = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
    }).format(date);
  };

  return (
    <NotesContainer {...props}>
      <Header>
        <Title>Workshop Notes</Title>
        <SaveIndicator className={isSaving ? 'saving' : ''}>
          {isSaving ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Saving...
            </>
          ) : lastSaved && (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              Last saved at {formatLastSaved(lastSaved)}
            </>
          )}
        </SaveIndicator>
      </Header>
      <TextArea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Take notes here..."
      />
    </NotesContainer>
  );
};

export default Notes;
